import { useMemo } from 'react';
import ArchiveIcon from '@mui/icons-material/Archive';
import { OfferTermsInvitation } from '@lendica/utils';

export type InviteActionType = 'send' | 'resend' | 'view' | 'archive';

export interface RowAction {
    type: InviteActionType;
    label: string;
    onClick: () => void;
    inMenu?: boolean;
    color?: 'primary' | 'secondary' | 'info' | 'success' | 'error' | 'warning';
    variant?: 'contained' | 'outlined' | 'text';
    icon?: any;
}

export interface OfferTermsActionCallbacks {
    onSendInvite: (row: OfferTermsInvitation) => void;
    onResendInvite: (row: OfferTermsInvitation) => void;
    onViewDetails: (row: OfferTermsInvitation) => void;
    onArchive: (row: OfferTermsInvitation) => void;
}

export function useOfferTermsActions(
    callbacks: OfferTermsActionCallbacks
): (row: OfferTermsInvitation) => RowAction[] {
    const actionMap = (row: OfferTermsInvitation): Record<InviteActionType, RowAction> =>
        ({
            send: {
                type: 'send',
                label: 'Send invite',
                onClick: () => callbacks.onSendInvite(row),
                color: 'primary',
                inMenu: false,
                variant: 'contained',
            },
            resend: {
                type: 'resend',
                label: 'Send reminder',
                onClick: () => callbacks.onResendInvite(row),
                color: 'primary',
                inMenu: false,
            },
            view: {
                type: 'view',
                label: 'View details',
                onClick: () => callbacks.onViewDetails(row),
                color: 'secondary',
                inMenu: false,
            },
            archive: {
                type: 'archive',
                label: 'Archive',
                icon: ArchiveIcon,
                onClick: () => callbacks.onArchive(row),
                inMenu: true,
            },
        } as unknown as Record<InviteActionType, RowAction>);
    return useMemo(() => {
        return (row: OfferTermsInvitation) => {
            const actions: RowAction[] = [];
            switch (row.status) {
                case 'NEW':
                    actions.push(actionMap(row).send, actionMap(row).view);
                    break;
                case 'SENT':
                    actions.push(actionMap(row).resend, actionMap(row).view);
                    break;
                case 'STARTED':
                case 'SUBMITTED':
                    break;
                case 'APPROVED':
                    actions.push(actionMap(row).archive);
                    break;
                case 'REJECTED':
                    actions.push(actionMap(row).archive);
                    break;
            }
            return actions;
        };
    }, [callbacks]);
}
